import { Injectable, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, map, take, takeUntil, withLatestFrom } from 'rxjs';
import { ComponentEnum, OrderTypeEnum } from '@core/enums';
import { IAbstractPresenter, ILanguageObject, IMessageError, IUserLocation } from '@core/interfaces';
import { Address, Credential, DeliveryZone } from '@core/models';
import { StoresProcessor } from '../../domain';
import { ClientService, GeolocationService, I18nService } from '@core/services';
import {
	CART_FIND_TOTALS,
	CLIENT_CREATE_ADDRESS,
	CLIENT_FIND_LOCATION_SUCCESS,
	CLIENT_UPDATE_APARTMENT,
	CLIENT_UPDATE_DELIVERY_INSTRUCTIONS,
	CLIENT_UPDATE_FORM,
} from '@store/actions';
import {
	selectApartmentOrSuite,
	selectAuth,
	selectClientLoading,
	selectClientLocation,
	selectDeliveryInstructions,
	selectDeliveryZoneAvailable,
	selectLanguaje,
	selectOrderType,
	selectStore,
} from '@store/selectors';
import { IAdressesLocatorComponent, IAdressesLocatorPresenter } from './interfaces';

@Injectable()
export class AdressesLocatorPresenter
	extends IAbstractPresenter<IAdressesLocatorComponent>
	implements IAdressesLocatorPresenter
{
	auth$: Observable<Credential | null>;
	orderType$: Observable<string | null>;
	address$: Observable<Address[]>;
	private autocompleteResultsSubject = new BehaviorSubject<google.maps.places.AutocompletePrediction[]>([]);
	autocompleteResults$: Observable<google.maps.places.AutocompletePrediction[]> =
		this.autocompleteResultsSubject.asObservable();
	autocompleteService?: google.maps.places.AutocompleteService;
	clientLocation$: Observable<IUserLocation | null>;
	isLoading$: Observable<boolean>;
	deliveryZoneAvailable$: Observable<DeliveryZone[] | null>;
	apartametOrSuite$: Observable<string | null | undefined>;
	deliveryInstructions$: Observable<string | null | undefined>;

	pickupAddress: string | null = null;
	formGroup: FormGroup;
	translations: ILanguageObject;
	errorMessages: IMessageError;
	searchAddressInput: string = '';
	idAddressDefault: string | null = null;
	showNameAddressInput: boolean = false;
	isLoggedIn: boolean = false;
	IsModal: boolean = false;
	isPickup: boolean = false;

	constructor(
		private store: Store,
		private formBuilder: FormBuilder,
		private modalCtrl: ModalController,
		private clientService: ClientService,
		private geolocationService: GeolocationService,
		private i18nService: I18nService,
		private storesProcessor: StoresProcessor
	) {
		super();

		this.auth$ = this.store.select(selectAuth);
		this.isLoading$ = this.store.select(selectClientLoading);
		this.orderType$ = this.store.select(selectOrderType);
		this.address$ = this.clientService.getAddress();
		this.clientLocation$ = this.store.select(selectClientLocation);
		this.apartametOrSuite$ = this.store.select(selectApartmentOrSuite);
		this.deliveryInstructions$ = this.store.select(selectDeliveryInstructions);
		this.deliveryZoneAvailable$ = this.store.select(selectDeliveryZoneAvailable);
		this.translations = this.i18nService.getTranslations(ComponentEnum.MultiStore);

		this.errorMessages = {
			apartment: {
				required: (): string => 'Apartment or suite is required',
				minlength: (error: ValidationErrors): string => `Must be at least ${error['requiredLength']} characters`,
				maxlength: (error: ValidationErrors): string => `Cannot exceed ${error['requiredLength']} characters`,
			},
			instructions: {
				minlength: (error: ValidationErrors): string => `Must be at least ${error['requiredLength']} characters`,
				maxlength: (error: ValidationErrors): string => `Cannot exceed ${error['requiredLength']} characters`,
			},
		};

		this.formGroup = this.formBuilder.group({
			address: [null],
			apartment: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(35)]],
			instructions: [null, [Validators.minLength(3), Validators.maxLength(35)]],
			save: [false],
			addressName: [null],
		});
	}

	dispatchActions(): void {
		// Actions are dispatched upon user interaction or location resolution
	}

	override onAfterViewInit(): void {
		this.setupAutocomplete();
	}

	private setupAutocomplete(): void {
		const attachAll = (): void => {
			this.view.locationInputs?.forEach(input => this.initAutocomplete(input, window));
			if (this.view.locationInput) {
				this.initAutocomplete(this.view.locationInput, window);
			}
		};

		attachAll();
		this.view.locationInputs?.changes.pipe(takeUntil(this.destroy$)).subscribe(attachAll);
	}

	suscribeObservers(): void {
		this.view.errorMessages = this.errorMessages;

		this.store
			.select(selectLanguaje)
			.pipe(takeUntil(this.destroy$))
			.subscribe(lang => {
				this.view.translations = this.i18nService.getTranslations(ComponentEnum.MultiStore, lang);
			});

		this.auth$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
			this.isLoggedIn = this.storesProcessor.isClientAuthenticated(auth);
			this.view.isLoggedIn = this.isLoggedIn;
		});

		this.clientService.closeDialog$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.showNameAddressInput = false;
			this.view.showNameAddressInput = false;
		});

		this.clientLocation$.pipe(take(1), takeUntil(this.destroy$)).subscribe(clientLocation => {
			if (!clientLocation?.address) {
				this.findUserLocation();
			} else {
				this.formGroup.get('address')?.setValue(clientLocation.address, { emitEvent: false });
				this.searchAddressInput = clientLocation.address ?? '';
				this.view.searchAddressInput = this.searchAddressInput;
			}
		});

		this.apartametOrSuite$.pipe(takeUntil(this.destroy$)).subscribe(apartametOrSuite => {
			this.formGroup.get('apartment')?.setValue(apartametOrSuite ?? '', { emitEvent: false });
		});

		this.deliveryInstructions$.pipe(takeUntil(this.destroy$)).subscribe(deliveryInstructions => {
			this.formGroup.get('instructions')?.setValue(deliveryInstructions ?? '', { emitEvent: false });
		});

		this.orderType$
			.pipe(
				withLatestFrom(this.store.select(selectApartmentOrSuite), this.store.select(selectDeliveryInstructions)),
				takeUntil(this.destroy$)
			)
			.subscribe(([orderType, apartment, instructions]) => {
				this.view.isPickup = OrderTypeEnum.Pickup === orderType;

				if (orderType === OrderTypeEnum.Delivery) {
					this.store
						.select(selectClientLocation)
						.pipe(take(1))
						.subscribe(location => {
							this.searchAddressInput = location?.address ?? '';
							this.view.searchAddressInput = this.searchAddressInput;
						});

					if (!this.searchAddressInput || this.searchAddressInput.length === 0) {
						this.findUserLocation();
					}

					this.formGroup.get('apartment')?.setValue(apartment ?? '', { emitEvent: false });
					this.formGroup.get('instructions')?.setValue(instructions ?? '', { emitEvent: false });
				}
			});
	}

	findUserLocation(): void {
		this.idAddressDefault = null;
		this.view.idAddressDefault = null;

		this.geolocationService
			.getUserLocation()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: location => {
					this.searchAddressInput = location?.address ?? '';
					this.view.searchAddressInput = this.searchAddressInput;
					this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location }));
				},
				error: err => {
					console.error('Error obteniendo ubicación del usuario:', err);
				},
			});
	}

	saveManualAddress(inputOrString?: HTMLInputElement | string | null): void {
		const value = typeof inputOrString === 'string' ? inputOrString : inputOrString?.value;

		this.idAddressDefault = null;
		this.view.idAddressDefault = null;
		this.searchAddressInput = value ?? '';
		this.view.searchAddressInput = this.searchAddressInput;

		if (this.storesProcessor.shouldGeocodeAddress(value, this.pickupAddress)) {
			this.formGroup.get('address')?.setValue(value);

			this.geolocationService
				.getCoordinatesFromAddress(value!)
				.pipe(takeUntil(this.destroy$))
				.subscribe(location => this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location })));
		}
	}

	selectAutocomplete(
		result: google.maps.places.AutocompletePrediction,
		inputElement?: ElementRef<HTMLInputElement | HTMLElement>
	): void {
		const address = result.description;

		if (inputElement?.nativeElement) {
			const nativeEl = inputElement.nativeElement;
			if ('value' in nativeEl) {
				(nativeEl as HTMLInputElement).value = address;
			} else {
				const innerInput = nativeEl.querySelector?.('input');
				if (innerInput) {
					innerInput.value = address;
				}
			}
		}

		this.autocompleteResultsSubject.next([]);
		this.searchAddressInput = address;
		this.view.searchAddressInput = address;
		this.pickupAddress = address;

		if (this.cdr) {
			this.cdr.detectChanges();
		}

		this.geolocationService
			.getCoordinatesFromAddress(address)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: location => {
					this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location }));
				},
				error: err => {
					console.error('Error getting coordinates for address:', err);
				},
			});
	}

	onAddressInputChange(event: Event | string, windowRef?: Window): void {
		const value = typeof event === 'string' ? event : (event.target as HTMLInputElement)?.value;
		this.saveManualAddress(value ?? '');
		this.searchAutocomplete(value ?? '', windowRef);
	}

	searchAutocomplete(value: string, windowRef?: Window): void {
		const places = windowRef?.google?.maps?.places;
		if (this.storesProcessor.isValidAddressQuery(value) && places) {
			if (!this.autocompleteService) {
				this.autocompleteService = new places.AutocompleteService();
			}
			this.autocompleteService.getPlacePredictions(
				{
					input: value,
					types: ['address'],
				},
				(predictions, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
						this.autocompleteResultsSubject.next(predictions);
					} else {
						this.autocompleteResultsSubject.next([]);
					}
					if (this.cdr) {
						this.cdr.detectChanges();
					}
				}
			);
		} else {
			this.autocompleteResultsSubject.next([]);
			if (this.cdr) {
				this.cdr.detectChanges();
			}
		}
	}

	clearAutocomplete(): void {
		this.autocompleteResultsSubject.next([]);
		if (this.cdr) {
			this.cdr.detectChanges();
		}
	}

	initAutocomplete(
		inputElement?: ElementRef<HTMLInputElement | HTMLElement>,
		windowRef?: Window
	): void {
		const places = windowRef?.google?.maps?.places;
		if (places && inputElement?.nativeElement && 'addEventListener' in inputElement.nativeElement) {
			inputElement.nativeElement.addEventListener('input', (event: Event) => {
				const value = (event.target as HTMLInputElement).value;
				if (this.storesProcessor.isValidAddressQuery(value)) {
					this.searchAutocomplete(value, windowRef);
				} else {
					this.clearAutocomplete();
				}
			});
		}
	}

	onApartmentOrSuiteChange(event: Event | string): void {
		const value = typeof event === 'string' ? event : (event.target as HTMLInputElement)?.value;
		this.store.dispatch(CLIENT_UPDATE_APARTMENT({ apartmentOrSuite: value ?? '' }));
	}

	onDeliveryInstructionsChange(event: Event | string): void {
		const value = typeof event === 'string' ? event : (event.target as HTMLTextAreaElement)?.value;
		this.store.dispatch(CLIENT_UPDATE_DELIVERY_INSTRUCTIONS({ deliveryInstructions: value ?? '' }));
	}

	onFavoriteChange(event: Event | boolean): void {
		const checked = typeof event === 'boolean' ? event : (event.target as HTMLInputElement)?.checked;
		this.showNameAddressInput = checked;
		this.view.showNameAddressInput = checked;
	}

	onAddressClick(address: Address): void {
		const addressString = this.storesProcessor.formatFullAddress(address);
		this.saveManualAddress(addressString);
	}

	updateDeliveryAddress(event: Event): void {
		const target = event.target as HTMLInputElement;
		const value = target.value;
		this.clientService
			.getAddressById(value)
			.pipe(takeUntil(this.destroy$), take(1))
			.subscribe(address => {
				this.geolocationService
					.getCoordinatesFromAddress(address?.direccion1)
					.pipe(takeUntil(this.destroy$), take(1))
					.subscribe(coordinates => {
						this.searchAddressInput = coordinates?.address ?? '';
						this.view.searchAddressInput = this.searchAddressInput;
						this.formGroup.get('address')?.setValue(coordinates?.address ?? '', { emitEvent: false });
						this.store.dispatch(CLIENT_FIND_LOCATION_SUCCESS({ location: coordinates }));
					});
			});
	}

	confirmDeliveryAddress(): void {
		if (this.formGroup.invalid) {
			this.formGroup.markAllAsTouched();
			return;
		}

		this.addressAvailable().pipe(take(1)).subscribe(isAddressAvailable => {
			this.store.dispatch(CART_FIND_TOTALS());

			const { save, apartment: apartamento, addressName: nombre } = this.formGroup.value;
			const clientAddressForm = this.storesProcessor.formatClientAddress(
				this.searchAddressInput ?? '',
				apartamento,
				nombre
			);

			this.store.dispatch(CLIENT_UPDATE_FORM({ clientAddressForm }));

			if (save) {
				this.store.dispatch(CLIENT_CREATE_ADDRESS());
			}

			const decision = this.storesProcessor.evaluateAddressConfirmation({
				isLoggedIn: this.isLoggedIn,
				showNameAddressInput: this.showNameAddressInput,
				isAddressAvailable,
			});

			if (decision.shouldDismissModal) {
				this.modalCtrl.dismiss();
			}

			if (decision.shouldResetForm) {
				this.formGroup.reset({
					address: null,
					apartment: null,
					instructions: null,
					save: false,
					addressName: null,
				}, { emitEvent: false });
			}
		});
	}

	addressAvailable(): Observable<boolean> {
		return combineLatest([
			this.deliveryZoneAvailable$,
			this.store.select(selectStore),
		]).pipe(
			map(([deliveryZonesAvailable, currentStore]) =>
				this.storesProcessor.isAddressAvailable(deliveryZonesAvailable, currentStore)
			)
		);
	}
}
